<!-- src/views/OnBoarding/SubContractorOnboardingView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left flex flex-col justify-between">
    
    <!-- 🛡️ PIXEL PERFECTION: Vertical Created-By Link (Matches App.vue global style) -->
    <a href="https://pixelperfection.co.uk" target="_blank" rel="noopener noreferrer" class="created-by-link no-print uppercase">Get Pixel Perfection</a>

    <div class="p-4 sm:p-6 md:p-8 flex-grow">
      <div class="max-w-7xl mx-auto pb-12 text-left">
        
        <!-- 1. MASTER COMMAND HEADER -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 md:p-12 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden">
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none"><ShieldCheckIcon class="h-32 w-32 text-white" /></div>
          <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 text-left">
            <div>
              <h1 class="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-none text-left">Sub-Contractor Onboarding Pack</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3 text-left">Supplier Evaluation Questionnaire • Registry V2.5</p>
            </div>
            <div v-if="hasDraft" class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3 backdrop-blur-md">
              <div class="w-2 h-2 bg-[#20FCF1] rounded-full animate-pulse"></div>
              <span class="text-white text-[10px] font-black uppercase tracking-widest text-left">Draft Progress Saved</span>
            </div>
          </div>
        </div>

        <!-- 2. MAIN FORM BODY -->
        <div class="bg-white p-6 sm:p-10 md:p-16 rounded-b-[40px] shadow-xl border-x border-b border-slate-100">
          
          <!-- Instructions Block -->
          <section v-if="isLoadingText || (mainTextContent && mainTextContent !== '<p><br></p>')" class="mb-16 p-10 bg-slate-50 rounded-[32px] border border-slate-100 shadow-inner text-left">
            <div v-if="isLoadingText" class="flex items-center gap-3 text-left">
               <div class="animate-spin h-4 w-4 border-2 border-[#20FCF1] border-t-transparent rounded-full text-left"></div>
               <span class="text-[10px] font-black uppercase text-slate-400 tracking-widest text-left">Synchronising Protocol Instructions...</span>
            </div>
            <div v-else class="ql-editor !p-0 text-slate-600 leading-relaxed font-medium text-left" v-html="mainTextContent"></div>
          </section>

          <!-- Error State -->
          <div v-if="initError" class="mb-10 p-10 bg-rose-50 border-2 border-rose-100 rounded-[40px] flex items-center gap-6 animate-fadeIn text-left">
             <ExclamationTriangleIcon class="h-10 w-10 text-rose-500 flex-shrink-0" />
             <div class="text-left">
               <p class="text-[10px] font-black uppercase tracking-widest text-rose-400 leading-none text-left">Registry Access Failure</p>
               <p class="text-sm font-bold text-rose-700 mt-2 uppercase tracking-tight text-left">{{ initError }}</p>
             </div>
          </div>

          <form v-if="!formSubmitted && !initError" @submit.prevent="submitForm" class="space-y-20 animate-fadeIn text-left">

            <!-- SECTION 1: COMPANY INFORMATION -->
            <section class="text-left">
              <div class="flex items-center gap-4 mb-10 text-left">
                <div class="bg-[#20FCF1] text-[#0A1630] w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl">
                  <IdentificationIcon class="h-7 w-7" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">1. General - Company Information</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 bg-slate-50 p-10 rounded-[32px] border border-slate-100 shadow-inner text-left">
                <div class="space-y-8 text-left">
                  <!-- 🛡️ LOCKED IN: Company Name is pre-populated and read-only for external invitees -->
                  <div class="text-left">
                    <label class="form-label text-left">Company Name *</label>
                    <input type="text" v-model="formData.companyInfo.companyName" readonly class="form-input font-black uppercase text-left bg-slate-100 text-slate-600 cursor-not-allowed" />
                  </div>
                  <div class="text-left"><label class="form-label text-left">Company Address *</label><textarea v-model="formData.companyInfo.companyAddress" required rows="3" class="form-input text-left"></textarea></div>
                  
                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
                    <div class="text-left"><label class="form-label text-left">Reg Number *</label><input type="text" v-model="formData.companyInfo.regNumber" required class="form-input text-left" /></div>
                    <div class="text-left"><label class="form-label text-left">VAT Number</label><input type="text" v-model="formData.companyInfo.vat" class="form-input text-left" /></div>
                    <div class="text-left"><label class="form-label text-left">UTR Number</label><input type="text" v-model="formData.companyInfo.utr" class="form-input text-left" /></div>
                  </div>
                </div>

                <div class="space-y-8 text-left">
                  <div class="text-left"><label class="form-label text-left">Website</label><input type="text" v-model="formData.companyInfo.website" class="form-input text-left" placeholder="www.company.com" /></div>
                  <div class="grid grid-cols-2 gap-4 text-left">
                    <div class="text-left"><label class="form-label text-left">Contact Name *</label><input type="text" v-model="formData.companyInfo.contactName" required class="form-input text-left" /></div>
                    <div class="text-left"><label class="form-label text-left">Position *</label><input type="text" v-model="formData.companyInfo.contactPosition" required class="form-input text-left" /></div>
                  </div>
                  <div class="grid grid-cols-2 gap-4 text-left">
                    <!-- 🛡️ LOCKED IN: Email Address is pre-populated and read-only for external invitees -->
                    <div class="text-left">
                      <label class="form-label text-left">Email Address *</label>
                      <input type="email" v-model="formData.companyInfo.contactEmail" readonly class="form-input font-bold text-left bg-slate-100 text-slate-600 cursor-not-allowed" />
                    </div>
                    <div class="text-left"><label class="form-label text-left">Telephone No. *</label><input type="tel" v-model="formData.companyInfo.contactPhone" required class="form-input text-left" /></div>
                  </div>
                </div>
              </div>
            </section>
            
            <!-- SECTION 1.2: SERVICES & QUALITY -->
            <section class="text-left">
              <div class="flex items-center gap-4 mb-10 text-left">
                <div class="bg-[#20FCF1] text-[#0A1630] w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl">
                  <CheckBadgeIcon class="h-7 w-7" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">1.2. Services & Quality Assurance</h2>
              </div>
              <div class="space-y-10 border-l-4 border-[#20FCF1] pl-10 text-left">
                <div class="text-left"><label class="form-label text-base mb-6 font-bold text-left">Does your Company undertake work in the Railway Sector?</label><RadioGroupYesNoNA v-model="formData.railwaySector.undertakesWork" name="rw_sector_undertakes" /></div>
                <div v-if="formData.railwaySector.undertakesWork === true" class="grid grid-cols-1 md:grid-cols-2 gap-10 p-10 bg-slate-50 rounded-[32px] animate-fadeIn text-left">
                  <div class="text-left"><label class="form-label text-left">Type of service offered</label><textarea v-model="formData.railwaySector.serviceSummary" rows="2" class="form-input text-left"></textarea></div>
                  <div class="text-left"><label class="form-label text-left">Years of experience</label><input type="number" v-model="formData.railwaySector.yearsExperience" class="form-input text-left" /></div>
                </div>
                <div class="pt-10 border-t border-slate-100 text-left">
                  <label class="form-label mb-4 font-bold text-left">Does your company have RISQS approved status?</label>
                  <RadioGroupYesNoNA v-model="formData.risqsStatus.isApproved" name="ris_stat_check" />
                  <div v-if="formData.risqsStatus.isApproved === true" class="mt-6 p-8 bg-[#EBFDF8] rounded-[32px] border-2 border-dashed border-[#20FCF1]/30 animate-fadeIn text-left">
                    <DocumentUploader id="up_risqs" label="Upload RISQS Certificate" @update:files="(files) => handleFileChange('risqsCertificate', files)" />
                  </div>
                </div>
                <div class="pt-10 border-t border-slate-100 text-left">
                  <label class="form-label mb-4 font-bold text-left">Certified to BS EN ISO 9001?</label>
                  <RadioGroupYesNoNA v-model="formData.qualityAssurance.isCertified" name="qa_iso_check" />
                  <div v-if="formData.qualityAssurance.isCertified === true" class="mt-6 p-8 bg-[#EBFDF8] rounded-[32px] border-2 border-dashed border-[#20FCF1]/30 animate-fadeIn text-left">
                    <DocumentUploader id="up_iso9001" label="Upload ISO 9001 Certificate" @update:files="(files) => handleFileChange('iso9001Certificate', files)" />
                  </div>
                </div>
              </div>
            </section>

            <!-- SECTION 2: HEALTH & SAFETY -->
            <section class="text-left">
              <div class="flex items-center gap-4 mb-10 text-left">
                <div class="bg-[#20FCF1] text-[#0A1630] w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl">
                  <ShieldCheckIcon class="h-7 w-7" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">2. Management of Health and Safety</h2>
              </div>
              <div class="space-y-12 text-left">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 text-left">
                  <div class="p-10 bg-slate-50 rounded-[40px] border border-slate-100 space-y-10 shadow-inner text-left">
                    <div class="text-left"><label class="form-label mb-4 font-bold text-left">Certified EMS (ISO 14001 / EMAS)?</label><RadioGroupYesNoNA v-model="formData.environmental.isCertified" name="env_iso_check" /><div v-if="formData.environmental.isCertified === true" class="mt-6 text-left"><DocumentUploader label="Upload EMS Cert" @update:files="(files) => handleFileChange('environmentalCertificate', files)" /></div></div>
                    <div class="pt-10 border-t border-slate-200 text-left"><label class="form-label mb-4 font-bold text-left">Refuse to Work policy in place?</label><RadioGroupYesNoNA v-model="formData.hsManagement.refuseToWork" name="rtw_check" /><div v-if="formData.hsManagement.refuseToWork === true" class="mt-6 text-left"><DocumentUploader label="Upload Policy" @update:files="(files) => handleFileChange('refuseToWorkPolicy', files)" /></div></div>
                  </div>
                  <div class="p-10 bg-slate-50 rounded-[40px] border border-slate-100 space-y-10 shadow-inner text-left">
                    <div class="text-left"><label class="form-label mb-4 font-bold text-left">Alcohol and Drugs policy?</label><RadioGroupYesNoNA v-model="formData.hsManagement.alcoholDrugs" name="ad_pol_check" /><div v-if="formData.hsManagement.alcoholDrugs === true" class="mt-6 text-left"><DocumentUploader label="Upload Policy" @update:files="(files) => handleFileChange('alcoholDrugsPolicy', files)" /></div></div>
                    <div class="pt-10 border-t border-slate-200 text-left"><label class="form-label mb-4 font-bold text-left">Modern Slavery Policy?</label><RadioGroupYesNoNA v-model="formData.hsManagement.modernSlavery" name="ms_pol_check" /><div v-if="formData.hsManagement.modernSlavery === true" class="mt-6 text-left"><DocumentUploader label="Upload Policy" @update:files="(files) => handleFileChange('modernSlaveryPolicy', files)" /></div></div>
                  </div>
                </div>
                <div class="bg-slate-50 p-10 rounded-[40px] border border-slate-100 shadow-inner text-left">
                  <h3 class="text-sm font-black uppercase tracking-widest text-slate-400 mb-8 text-left">Safety Performance Data</h3>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-12 text-left">
                    <div class="text-left"><label class="form-label text-left">Fatalities / RIDDORS - Past 3 Years</label><textarea v-model="formData.hsManagement.riddorsDetails" rows="4" class="form-input text-left" placeholder="List details or type 'None'"></textarea></div>
                    <div class="text-left"><label class="form-label text-left">Accident Statistics - Last 5 Years</label><textarea v-model="formData.hsManagement.accidentStats" rows="4" class="form-input text-left"></textarea></div>
                  </div>
                </div>
              </div>
            </section>

            <!-- SECTION 2.2: INSURANCE POLICIES -->
            <section class="text-left">
              <div class="flex items-center gap-4 mb-8 text-left">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <ClipboardDocumentCheckIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none text-left">2.2. Insurance Policies</h2>
              </div>

              <div class="overflow-x-auto rounded-[40px] border border-slate-200 shadow-2xl bg-white text-left">
                <table class="min-w-full divide-y divide-slate-200 text-left">
                  <thead class="bg-[#0A1630] text-left">
                    <tr>
                      <th class="px-8 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Insurance Type</th>
                      <th class="px-8 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Held?</th>
                      <th class="px-8 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Insurer</th>
                      <th class="px-8 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Limit (£)</th>
                      <th class="px-8 py-6 text-left text-[10px] font-black text-[#20FCF1] uppercase tracking-widest">Expiry Date</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 text-left">
                    <tr v-for="(label, key) in insuranceMapping" :key="key" class="hover:bg-slate-50 transition-colors text-left">
                      <td class="px-8 py-5 text-[11px] font-black text-slate-700 uppercase bg-slate-50/50 min-w-[220px] text-left">{{ label }}</td>
                      <td class="px-4 py-4 min-w-[140px] text-left">
                        <select v-model="formData.insuranceGrid[key].held" class="form-input !h-14 !py-3 text-[11px] font-black uppercase appearance-none text-left">
                          <option :value="null">Select</option>
                          <option :value="true">Yes</option>
                          <option :value="false">No</option>
                          <option value="N/A">N/A</option>
                        </select>
                      </td>
                      <td class="px-4 py-4 min-w-[180px] text-left"><input type="text" v-model="formData.insuranceGrid[key].insurer" placeholder="Provider" class="form-input !h-14 text-xs font-bold text-left" /></td>
                      <td class="px-4 py-4 min-w-[150px] text-left"><input type="text" v-model="formData.insuranceGrid[key].limit" placeholder="e.g. 10m" class="form-input !h-14 text-xs font-bold text-left" /></td>
                      <td class="px-4 py-4 min-w-[160px] text-left"><input type="date" v-model="formData.insuranceGrid[key].expiry" class="form-input !h-14 text-xs font-bold text-left" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="mt-8 p-10 bg-[#EBFDF8] rounded-[40px] border-2 border-dashed border-[#20FCF1]/30 text-left">
                <DocumentUploader id="up_ins_bulk" label="Upload Insurance Portfolio Evidence" @update:files="(files) => handleFileChange('insurancePolicies', files)" />
              </div>
            </section>

            <!-- SECTION 3: BYPASS LOGIC -->
            <section class="bg-[#0A1630] p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden text-left">
              <div class="absolute top-0 right-0 p-12 opacity-10 pointer-events-none"><CheckBadgeIcon class="h-44 w-44" /></div>
              <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-10 text-left">
                <div class="max-w-2xl text-left">
                  <h3 class="text-2xl font-black uppercase tracking-tight text-[#20FCF1] text-left">3. Certification Status Check</h3>
                  <p class="text-slate-400 text-sm mt-4 font-medium leading-relaxed text-left">If you hold both <span class="text-white font-bold">ISO 9001</span> and <span class="text-white font-bold">RISQS</span> approved status, you may bypass the manual evaluation section below.</p>
                </div>
                <div class="bg-white/5 p-8 rounded-[40px] border border-white/10 flex flex-col items-center text-center">
                  <label class="text-[10px] font-black uppercase tracking-[0.3em] text-[#20FCF1] mb-6 text-center">Do you hold both statuses?</label>
                  <div class="flex gap-6 text-center">
                    <button type="button" @click="formData.step3Bypass = true" :class="formData.step3Bypass === true ? 'bg-[#20FCF1] text-[#0A1630]' : 'bg-slate-700 text-white'" class="px-12 py-4 rounded-2xl font-black uppercase text-xs transition-all shadow-xl text-center">Yes</button>
                    <button type="button" @click="formData.step3Bypass = false" :class="formData.step3Bypass === false ? 'bg-[#20FCF1] text-[#0A1630]' : 'bg-slate-700 text-white'" class="px-12 py-4 rounded-2xl font-black uppercase text-xs transition-all shadow-xl text-center">No</button>
                  </div>
                </div>
              </div>
            </section>

            <!-- SECTION 4: MANUAL EVALUATION -->
            <section v-if="formData.step3Bypass === false" class="bg-slate-50 border-2 border-slate-200 p-10 rounded-[40px] animate-fadeIn space-y-12 shadow-inner text-left">
              <div class="border-b border-slate-200 pb-4">
                <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">4. Detailed Evaluation Questionnaire</h3>
                <p class="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1">Please complete all 13 compliance subsections below.</p>
              </div>

              <div v-for="cat in s4Structure" :key="cat.key" class="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm mb-10 last:mb-0 text-left">
                <h4 class="text-lg font-black text-[#0A1630] uppercase flex items-center gap-4 mb-8 border-b pb-5 text-left"><component :is="cat.icon" class="h-7 w-7 text-[#20FCF1]" /> {{ cat.title }}</h4>
                <div class="space-y-8 text-left">
                  <div v-for="q in cat.questions" :key="q.key" class="flex flex-col xl:flex-row xl:items-center justify-between gap-6 p-6 hover:bg-slate-50 rounded-2xl transition-colors border border-transparent hover:border-slate-100 text-left">
                    <p class="text-sm font-bold text-slate-700 max-w-2xl leading-relaxed text-left">{{ q.text }}</p>
                    <RadioGroupYesNoNA v-model="formData.s3[cat.key][q.key]" :name="`s3_${cat.key}_${q.key}`" />
                  </div>
                </div>
              </div>
            </section>

            <!-- SECTION 5: DOCUMENT CHECKLIST -->
            <section class="text-left">
              <div class="flex items-center gap-4 mb-8 text-left">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <DocumentTextIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none text-left">5. Document Checklist</h2>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 bg-slate-50 p-10 rounded-[40px] shadow-inner text-left">
                  <div v-for="(val, key) in checklistLabels" :key="key" class="flex items-start p-6 bg-white rounded-[24px] border border-slate-200 hover:border-[#20FCF1] transition-all shadow-sm group text-left">
                    <input :id="key" type="checkbox" v-model="formData.documents[key]" class="h-6 w-6 text-[#0A1630] border-slate-300 rounded-lg focus:ring-[#20FCF1] mt-0.5 cursor-pointer text-left">
                    <label :for="key" class="ml-4 block text-[11px] font-black text-slate-600 leading-tight uppercase tracking-wider group-hover:text-[#0A1630] cursor-pointer text-left">{{ val }}</label>
                  </div>
              </div>
              <div class="mt-8 p-12 bg-[#EBFDF8] rounded-[40px] border-2 border-dashed border-[#20FCF1]/30 text-center">
                <h4 class="text-sm font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 text-left ml-2">Upload Supporting Corporate Evidence</h4>
                <DocumentUploader id="checklist_bulk" label="Authorise Multi-File Upload" @update:files="(files) => handleFileChange('checklistAdditional', files)" />
              </div>
            </section>

            <!-- SECTION 6: DECLARATION -->
            <section class="bg-slate-900 p-10 md:p-16 rounded-[40px] text-white shadow-2xl text-left relative overflow-hidden">
              <div class="absolute -right-20 -bottom-20 opacity-10 pointer-events-none"><ShieldCheckIcon class="h-[500px] w-[500px] text-[#20FCF1]" /></div>
              <h2 class="text-2xl font-black text-[#20FCF1] uppercase tracking-tight mb-12 text-left">6. Authorised Declaration</h2>

              <div class="space-y-12 relative z-10 text-left">
                <div class="flex items-start gap-6 p-8 bg-white/5 rounded-[32px] border border-white/10 text-left">
                    <input id="declarationIsTrue" type="checkbox" v-model="formData.declaration.isTrue" required class="h-8 w-8 text-[#20FCF1] border-white/20 rounded-xl mt-1 cursor-pointer text-left">
                    <label for="declarationIsTrue" class="text-lg font-bold text-slate-200 leading-relaxed ml-4 text-left">I formally confirm that the responses provided are true, accurate, and valid. <span class="text-[#20FCF1]">*</span></label>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-16 text-left">
                  <div class="space-y-8 text-left">
                    <div class="text-left"><label class="text-[10px] font-black uppercase text-[#20FCF1] text-left">Full Legal Name</label><input type="text" v-model="formData.declaration.name" required class="w-full bg-white/5 border-white/10 text-white rounded-2xl p-5 mt-2 font-black uppercase text-left" /></div>
                    <div class="text-left"><label class="text-[10px] font-black uppercase text-[#20FCF1] text-left">Company Position</label><input type="text" :value="userPositionDisplay" readonly class="w-full bg-white/10 border-white/10 text-slate-400 rounded-xl p-4 mt-2 italic cursor-not-allowed uppercase text-left" /></div>
                  </div>
                  
                  <div class="space-y-6 text-center">
                    <label class="text-[10px] font-black uppercase text-[#20FCF1] text-center">Digital Signature Seal <span class="text-red-500">*</span></label>
                    <div class="bg-white rounded-[32px] p-8 shadow-inner border border-slate-100 text-center">
                        <div v-if="formData.declaration.signatureDataUrl" class="mb-6 flex items-center justify-center gap-2 bg-[#EBFDF8] px-5 py-2 rounded-full border border-[#20FCF1]/30 animate-fadeIn text-center">
                          <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
                          <span class="text-[9px] font-black uppercase text-[#0A1630] tracking-widest text-center">Identity Handshake Verified</span>
                        </div>

                        <div v-if="!signatureImagePreview">
                            <Vue3Signature ref="signaturePad" :sigOption="signatureOptions" :h="'220px'" :w="'100%'" class="border-2 border-dashed border-slate-100 rounded-2xl text-left"></Vue3Signature>
                        </div>
                        <div v-else class="p-6 bg-slate-50 rounded-2xl border border-dashed border-slate-200 mb-4 flex justify-center text-center">
                            <img :src="signatureImagePreview" class="max-h-24 object-contain grayscale text-center" alt="Seal Preview" />
                        </div>
                        
                        <div class="mt-6 flex flex-col gap-3 text-center">
                            <template v-if="!signatureImagePreview">
                                <button type="button" @click="saveSignature" class="w-full bg-[#20FCF1] text-[#0A1630] py-4 rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg text-center">Confirm Hand-Sign</button>
                            </template>
                            <button type="button" @click="clearSignature" class="w-full bg-slate-50 text-slate-300 py-3 rounded-xl font-black uppercase text-[9px] tracking-widest hover:text-rose-500 transition-all text-center">Clear / Reset Seal</button>
                        </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            
            <div class="pt-10 flex flex-col md:flex-row gap-4 text-center">
              <button type="submit" :disabled="isSubmitting || !isFormFullyValid" class="btn-premium flex-1 justify-center items-center gap-3 text-center">
                <span v-if="isSubmitting" class="flex items-center gap-3 text-center">
                  <div class="animate-spin h-5 w-5 border-2 border-[#0A1630] border-t-transparent rounded-full"></div>
                  Synchronising Registry Protocol...
                </span>
                <span v-else class="text-center">Authorise & Submit Onboarding Pack</span>
              </button>
            </div>
          </form>

          <!-- SUCCESS STATE -->
          <div v-if="formSubmitted" class="py-32 text-center animate-fadeIn text-center">
              <CheckBadgeIcon class="h-24 w-24 text-[#20FCF1] mx-auto mb-8 text-center" />
              <h2 class="text-4xl font-black text-[#0A1630] uppercase tracking-tight leading-none text-center">Induction Synchronised</h2>
              <p class="text-slate-500 font-bold uppercase text-xs tracking-widest mt-6 max-w-2xl mx-auto italic leading-relaxed text-center">Your corporate entity has been successfully recorded.</p>
              <div class="mt-12 bg-slate-50 border-2 border-dashed border-slate-200 p-12 rounded-[48px] max-w-xl mx-auto shadow-inner text-center">
                 <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none text-center">Protocol Reference</p>
                 <p class="text-3xl font-black text-[#0A1630] uppercase mt-5 tracking-tight text-center">{{ submissionRef }}</p>
              </div>
          </div>

          <div v-if="errorMessage" class="mt-8 p-10 bg-[#EF4444]/5 text-[#EF4444] border-2 border-[#EF4444]/10 rounded-[40px] flex items-center gap-6 shadow-xl animate-fadeIn text-left">
             <ExclamationTriangleIcon class="h-10 w-10 text-[#EF4444] flex-shrink-0" />
             <p class="text-sm font-bold text-[#EF4444] uppercase tracking-tight text-left">{{ errorMessage }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 🛡️ PIXEL PERFECTION: Official Pixel Logo Footer & Vertical Link (Matches App.vue Sister Style) -->
    <a href="https://pixelperfection.co.uk" target="_blank" rel="noopener noreferrer" class="created-by-link no-print uppercase">Get Pixel Perfection</a>

    <footer class="bg-white py-12 border-t border-slate-100 mt-auto no-print w-full relative z-10 flex flex-col items-center gap-6 select-none">
      <img 
        src="/images/pixel-logo.png" 
        alt="Pixel Perfection Logo" 
        class="h-10 object-contain select-none pointer-events-none" 
      />

      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 text-center space-y-6">
        <p class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] leading-none">
          Copyright &copy; 2026 Sentinel Safe™. All Rights Reserved.
        </p>
        <div class="text-[9px] font-black text-slate-300 uppercase tracking-[0.3em] leading-none space-y-3">
          <p>Sentinel Safe™ is operated and managed by Get Pixel Perfection Limited.</p>
          <p>Get Pixel Perfection Limited is a company registered in England and Wales. Company Number: 16854093.</p>
          <p>UK GDPR Compliant • <a href="https://pixelperfection.co.uk/privacy.html" target="_blank" rel="noopener noreferrer" class="hover:text-slate-500 transition-colors duration-200">Privacy Policy</a></p>
        </div>
      </div>
    </footer>

  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { db, storage, firebaseApp } from '@/firebaseConfig'; 
import { doc, getDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions'; 
import Vue3Signature from 'vue3-signature';
import RadioGroupYesNoNA from '@/components/forms/RadioGroupYesNoNA.vue';
import DocumentUploader from '@/components/DocumentUploader.vue';

import { 
  AcademicCapIcon, CheckBadgeIcon, DocumentTextIcon, ExclamationTriangleIcon, 
  ShieldCheckIcon, IdentificationIcon, LifebuoyIcon, WrenchScrewdriverIcon, 
  BeakerIcon, TruckIcon, ClockIcon, GlobeEuropeAfricaIcon, ClipboardDocumentCheckIcon 
} from '@heroicons/vue/24/outline';

const route = useRoute();
const userStore = useUserStore();
const functions = getFunctions(firebaseApp, 'europe-west2'); 

const mainTextContent = ref('');
const isLoadingText = ref(true);
const initError = ref('');
const errorMessage = ref('');
const isSubmitting = ref(false);
const formSubmitted = ref(false);
const submissionRef = ref('');
const hasDraft = ref(false);
const clientContextId = ref(null);
const targetProfileId = ref(null);

const signaturePad = ref(null);
const signatureImagePreview = ref(null);
const signatureOptions = ref({ penColor: "#0A1630", backgroundColor: "rgba(255,255,255,0)" });

const insuranceMapping = { contractors: 'Contractors (all risks)', employers: 'Employers Liability', other: 'Other Insurance', plant: 'Plant', products: 'Products Liability', professional: 'Professional Indemnity', public: 'Public Liability' };
const checklistLabels = { orgChart: 'Organisation Chart', isoCertification: 'ISO Certs', risqsCertificate: 'RISQS Cert', hsPolicy: 'H&S Policy', qualityPolicy: 'Quality Policy', environmentPolicy: 'Env Policy', alcoholDrugsPolicy: 'A&D Policy', workSafePolicy: 'Work Safe', workingHoursProcedure: 'Work Hours', equalityDiversityPolicy: 'Diversity', insurances: 'Insurances', othersNotices: 'Notices', hsAdvisorDetails: 'Advisor CV', cyberSecurity: 'Cyber Security' };

const s4Structure = [
    { key: 'training', title: 'Training & Induction', icon: AcademicCapIcon, questions: [{key:'induction', text:'Induction training provided?'}, {key:'recordsMaintained', text:'Records maintained?'}, {key:'requirementsReviewed', text:'Requirements reviewed?'}]},
    { key: 'quality', title: 'Quality Assurance', icon: CheckBadgeIcon, questions: [{key:'policyStatement', text:'Quality policy statement active?'}, {key:'manualProcedures', text:'Manual & Procedures available?'}, {key:'reviewedAudited', text:'Regularly reviewed/audited?'}]},
    { key: 'documentControl', title: 'Document Control', icon: DocumentTextIcon, questions: [{key:'revisionStatus', text:'Numbering & Revision status used?'}, {key:'checkingSystem', text:'System for approving outgoing docs?'}, {key:'filingSystem', text:'Transmittal/Filing system operation?'}]},
    { key: 'nonConformance', title: 'Non-Conformance', icon: ExclamationTriangleIcon, questions: [{key:'procedure', text:'Procedure for non-conformance?'}, {key:'recordsMaintained', text:'Records of corrective actions kept?'}, {key:'complaintsProcedure', text:'Client complaints procedure active?'}]},
    { key: 'hsManagement', title: 'Health & Safety', icon: ShieldCheckIcon, questions: [{key:'policyStatement', text:'H&S Policy Statement held?'}, {key:'reviewProcedures', text:'Procedures for reviewing system?'}, {key:'riskAssessment', text:'Risk Assessment process active?'}]},
    { key: 'cms', title: 'Competency Management', icon: IdentificationIcon, questions: [{key:'hasCms', text:'Documented CMS for work activities?'}, {key:'assessmentMethods', text:'Documented in-house assessments?'}, {key:'idCards', text:'Staff issued with company ID cards?'}]},
    { key: 'ae', title: 'Incident Protocol', icon: LifebuoyIcon, questions: [{key:'riddorProcedure', text:'Procedure for RIDDOR 2013?'}, {key:'firstAidWelfare', text:'Adequate First Aid & Welfare?'}]},
    { key: 'ppe', title: 'PPE', icon: WrenchScrewdriverIcon, questions: [{key:'procedure', text:'Procedure for issuing/checking PPE?'}]},
    { key: 'ad', title: 'Alcohol & Drugs', icon: BeakerIcon, questions: [{key:'preEmploymentScreening', text:'Pre-employment screening undertaken?'}, {key:'unannouncedTesting', text:'Unannounced testing active?'}, {key:'positiveResultProcedure', text:'Procedure for positive results?'}]},
    { key: 'pe', title: 'Plant & Equipment', icon: TruckIcon, questions: [{key:'inspectionMaintenance', text:'P&E regularly inspected?'}, {key:'calibrationCertification', text:'Calibration & Test certification held?'}]},
    { key: 'wh', title: 'Working Hours', icon: ClockIcon, questions: [{key:'monitoringProcedure', text:'Procedure for monitoring unplanned exceedance?'}]},
    { key: 'env', title: 'Environmental', icon: GlobeEuropeAfricaIcon, questions: [{key:'policyStatement', text:'Environmental Policy statement held?'}, {key:'hasManager', text:'Responsible Manager appointed?'}]},
    { key: 'compliance', title: 'Compliance', icon: ClipboardDocumentCheckIcon, questions: [{key:'hasPerson', text:'Appointed person for compliance?'}]}
];

const createInitialData = () => ({
  step3Bypass: null,
  userName: '', userEmail: '',
  companyInfo: { companyName: '', regNumber: '', vat: '', utr: '', companyAddress: '', website: '', contactName: '', contactPosition: '', contactEmail: '', contactPhone: '' },
  insuranceGrid: Object.keys(insuranceMapping).reduce((acc, k) => ({ ...acc, [k]: { held: null, insurer: '', limit: '', basis: null, expiry: '' } }), {}),
  railwaySector: { undertakesWork: null, serviceSummary: '', yearsExperience: null },
  risqsStatus: { isApproved: null },
  qualityAssurance: { isCertified: null },
  hsManagement: { riddorsDetails: '', accidentStats: '', refuseToWork: null, alcoholDrugs: null, modernSlavery: null },
  environmental: { isCertified: null },
  s3: s4Structure.reduce((acc, cat) => ({ ...acc, [cat.key]: cat.questions.reduce((qAcc, q) => ({...qAcc, [q.key]: null}), {}) }), {}),
  documents: Object.keys(checklistLabels).reduce((acc, k) => ({ ...acc, [k]: false }), {}),
  declaration: { isTrue: false, signatureDataUrl: '', name: '', position: '', date: new Date().toISOString().slice(0, 10) }
});

const formData = reactive(createInitialData());
const localFiles = reactive({});
const STORAGE_KEY = 'sentinel_subcontractor_pack_draft';

const saveDraft = () => { localStorage.setItem(STORAGE_KEY, JSON.stringify(formData)); hasDraft.value = true; };
const loadDraft = () => { 
  const saved = localStorage.getItem(STORAGE_KEY); 
  if (saved) { 
    const parsed = JSON.parse(saved);
    const lockedCompanyName = formData.companyInfo.companyName;
    const lockedEmail = formData.companyInfo.contactEmail;

    Object.assign(formData, parsed); 

    if (lockedCompanyName) {
      formData.companyInfo.companyName = lockedCompanyName;
    }
    if (lockedEmail) {
      formData.companyInfo.contactEmail = lockedEmail;
    }

    hasDraft.value = true; 
  } 
};

/** 🛡️ RESOLVES SUB-CONTRACTOR CONTEXT WITHOUT PERMISSION CONSTRAINTS */
const loadRegistryContext = async () => {
  const token = route.params.id; 
  if (!token) {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (cid) {
        clientContextId.value = cid;
        try {
            const clientSnap = await getDoc(doc(db, 'clients', cid));
            if (clientSnap.exists()) mainTextContent.value = clientSnap.data().onboardingFormTexts?.subContractorPack || '';
        } catch (e) { /* silent */ }
    }
    isLoadingText.value = false;
    return;
  }
  
  const tokenParts = token.split('_');
  const extractedCid = tokenParts.length > 1 ? tokenParts[0] : null;
  if (extractedCid) {
    clientContextId.value = extractedCid;
  }

  try {
    // 🛡️ Call Cloud Function to resolve context with administrative privileges
    const getContextFn = httpsCallable(functions, 'getPublicSubContractorContext');
    const result = await getContextFn({ token });

    if (result.data?.success) {
      if (result.data.clientId) clientContextId.value = result.data.clientId;
      if (result.data.targetProfileId) targetProfileId.value = result.data.targetProfileId;
      if (result.data.companyName) formData.companyInfo.companyName = result.data.companyName;
      if (result.data.contactEmail) formData.companyInfo.contactEmail = result.data.contactEmail;
      if (result.data.mainTextContent) mainTextContent.value = result.data.mainTextContent;
    }
  } catch (err) { 
    console.warn("Public Cloud Function context lookup warning, attempting public registry fallback:", err);
    try {
      const regSnap = await getDoc(doc(db, 'publicOnboardingRegistry', token));
      if (regSnap.exists()) {
        const data = regSnap.data();
        if (data.clientId) clientContextId.value = data.clientId;
        targetProfileId.value = data.targetProfileId || data.userId || regSnap.id;
        const compName = data.companyName || data.clientName || data.name || data.company;
        if (compName) formData.companyInfo.companyName = compName;
        if (data.email) formData.companyInfo.contactEmail = data.email;
      }
    } catch (fallbackErr) {
      console.warn("Public registry fallback warning:", fallbackErr);
    }
  }
  finally { 
    isLoadingText.value = false;
    loadDraft(); 
  }
};

const handleFileChange = (key, files) => { localFiles[key] = Array.from(files); };

const userPositionDisplay = computed(() => {
  const val = userStore.userProfile?.jobTitle || formData.companyInfo.contactPosition;
  return val ? val.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Representative';
});

const isFormFullyValid = computed(() => {
    const info = formData.companyInfo;
    const isBasicValid = !!(info.companyName && info.regNumber && info.companyAddress && info.contactName && info.contactEmail && info.contactPhone);
    return isBasicValid && formData.step3Bypass !== null && formData.declaration.isTrue && !!formData.declaration.signatureDataUrl && !isLoadingText.value;
});

const saveSignature = () => { if (signaturePad.value) { const data = signaturePad.value.save('image/png'); if (data) { formData.declaration.signatureDataUrl = data; saveDraft(); } } };
const clearSignature = () => { if (signaturePad.value) signaturePad.value.clear(); signatureImagePreview.value = null; formData.declaration.signatureDataUrl = ''; saveDraft(); };

/** 🛡️ ATOMIC UPLOAD PROTOCOL */
const uploadAtomicFiles = async (cid) => {
    const evidenceMapping = {};
    for (const key of Object.keys(localFiles)) {
        const files = localFiles[key];
        if (!files || files.length === 0) continue;
        const urls = await Promise.all(files.map(async (file) => {
            const sRef = storageRef(storage, `clients/${cid}/subContractorOnboarding/atomic_${Date.now()}/${key}/${file.name.replace(/[^a-z0-9.]/gi, '_')}`);
            await uploadBytes(sRef, file);
            return getDownloadURL(sRef);
        }));
        evidenceMapping[`evidence_${key}`] = urls;
    }
    return evidenceMapping;
};

const submitForm = async () => {
  if (!isFormFullyValid.value) return;
  isSubmitting.value = true;
  errorMessage.value = '';
  
  try {
    let cid = clientContextId.value;
    if (!cid && route.params.id) {
      const parts = route.params.id.split('_');
      if (parts.length > 1) cid = parts[0];
    }
    if (!cid) cid = userStore.clientId || 'default_client';

    const token = route.params.id; 
    const suffix = token ? token.split('_').pop() : Math.random().toString(36).substring(2, 8);
    // 🛡️ IMMUTABLE VERSIONED AUDIT: Appending timestamp prevents overwriting repeated submissions
    const deterministicId = suffix ? `sub_pack_${suffix}_${Date.now()}` : `sub_pack_${Date.now()}`;

    const uploadedEvidence = await uploadAtomicFiles(cid);
    const targetUid = targetProfileId.value || userStore.userProfile?.userId;

    const submissionPayload = {
        ...formData,
        ...uploadedEvidence,
        isPublicSubmission: true, 
        clientUid: cid, 
        clientId: cid,
        userId: targetUid || null,
        userName: formData.companyInfo.contactName || formData.companyInfo.companyName,
        userEmail: String(formData.companyInfo.contactEmail).toLowerCase().trim(),
        submittedAt: new Date().toISOString(),
        status: 'pending review',
        type: 'sub-contractor',
        targetProfileId: targetUid || null
    };

    // 🛡️ Symmetrical Cloud Function submission with atomic counter increment
    const submitFn = httpsCallable(functions, 'submitGenericForm');
    const result = await submitFn({
      formIdentifier: 'subContractorPack',
      targetCollection: 'onboardingFormSubmissions',
      forcedDocumentId: deterministicId, 
      submissionData: submissionPayload
    });

    if (result.data?.success) {
      submissionRef.value = result.data.referenceNumber;
      localStorage.removeItem(STORAGE_KEY);
      formSubmitted.value = true;
    } else {
      throw new Error(result.data?.message || "Sync failure.");
    }

  } catch (e) {
    errorMessage.value = "Submission Failed: Company not recognised. Please verify your client registry link.";
    console.error("SubContractor Onboarding Submission Error:", e);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => { 
    loadRegistryContext(); 
});
</script>

<style scoped>
.form-input { @apply block w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-[#0A1630] shadow-inner focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all text-left; }
.form-label { @apply block text-[10px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2 ml-1 text-left; }
.btn-premium { @apply w-full bg-[#20FCF1] text-[#0A1630] py-7 rounded-[32px] text-xl font-black uppercase tracking-widest shadow-2xl hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center; }
.animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
:deep(.ql-editor) { font-size: 0.95rem; line-height: 1.8; color: #334155; text-align: left; }
th { border-bottom: 2px solid rgba(56, 227, 176, 0.1); text-align: left; }

/* 🛡️ PIXEL PERFECTION: Vertical Side Link Styling (Exact App.vue match) */
.created-by-link { position: fixed; bottom: 2.5rem; left: 2.5rem; z-index: 1000; transform-origin: bottom left; white-space: nowrap; color: #CBD5E1; font-size: 0.65rem; font-weight: 900; letter-spacing: 0.25em; text-decoration: none; transform: rotate(-90deg); opacity: 0.5; transition: all 0.3s ease-in-out; }
.created-by-link:hover { opacity: 1; color: #FD5E00; }
</style>